//******************************************************************************
/**
 * @fileoverview PointCloudPngParser - PointCloudPNG解析クラス
 * @author KITAO Kaoru <kaoru@kitao.net>
 * @version 0.4.0
 * @since 2022-10-28
 * @copyright 2022 KITAO Kaoru
 * @license MIT
 * @requires ListPngParser
 * * @history
 * - 2026-02-24 v0.4.0 型の厳密定義
 * - 2026-02-03 v0.3.2 体裁調整
 * - 2026-01-28 v0.3.1 RecordAnalyzerインスタンス導入により名前処理変更他
 * - 2026-01-27 v0.3.0 名称変更（旧PointCloudPng）
 * - 2026-01-26 v0.2.1 ListPngのメソッド名変更に対する調整
 * - 2026-01-25 v0.2.0 subarray導入のListPngに合わせて改善・機能スリム化
 * - 2023-01-06 v0.1.1 微調整
 * - 2022-10-28 v0.1.0 リリース
 */

//******************************************************************************
// import
//------------------------------------------------------------------------------
// 継承元クラス・使用クラス
import { ListPngParser } from "./ListPngParser.js";
import { RecordAnalyzer } from "./RecordAnalyzer.js";

//******************************************************************************
/**
 * クラス定義
 * @extends ListPngParser
 */
class PointCloudPngParser extends ListPngParser {
	//**************************************************************************
	/**
	 * 継承元メソッドを拡張
	 * @param {Uint8Array|Uint8ClampedArray} rawData RGBAの型付配列
	 * @returns {this}
	 */
	parse(rawData) {
		//----------------------------------------------------------------------
		// 親クラスのメソッドを実行
		super.parse(rawData);
		//----------------------------------------------------------------------
		// カスタムヘッダーを使用して処理（一旦配列から取り出す）
		const customHeaders = this.find("custom");
		this.headers.pop();
		if (!customHeaders.value) {
			throw new Error('custom header value is undefined');
		}
		// subType用のrgbaを取り出す
		const values = /** @type {Uint8Array|Uint8ClampedArray} */ (
			customHeaders.value
		).subarray(0, this.ELEMENTS);
		// rgba指定の例外チェック
		if (values[3] === 0) {
			throw new Error(
				`Invalid alpha value 0 for header "subType"; expected 255.`
			);
		}
		//----------------------------------------------------------------------
		// 各カラムの名前を決める処理はr値の後方4ビットを使う
		/** @type {Array<[string, number]>} */
		const entryDefs = [
			["x", 1],
			["y", 1],
			["z", (values[0] >> 3) & 1],
			["color", (values[0] >> 2) & 1],
			["id", (values[0] >> 1) & 1],
			["class", values[0] & 1],
		];
		/** @type {string[]} */
		const entries = entryDefs.filter(([_, flag]) => {
			return flag === 1;
		}).map(([name, _]) => {
			return name;
		});
		//----------------------------------------------------------------------
		// カラムに名前を与える（undefinedはユーザ定義カラム）
		/** @type {RecordAnalyzer[]} */
		const columns = /** @type {RecordAnalyzer[]} */ (this.find("columns").value);
		columns.forEach((value, index) => {
			value.setName(entries[index]);
		});
		//----------------------------------------------------------------------
		// カスタムヘッダーを書き戻す
		const custom = structuredClone(customHeaders);
		custom.pixelCount -= 1;
		if (!custom.value) {
			custom.value = new Uint8Array(0);
		} else {
			custom.value = /** @type {Uint8Array|Uint8ClampedArray} */ (
				custom.value
			).subarray(this.ELEMENTS);
		}
		// 書き戻し処理
		this.headers.push({
			name: "subType",
			pixelCount: 1,
			type: "rgba",
			value: values.subarray(0, this.ELEMENTS)
		}, custom);
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * name属性未定義のカラムインデックスを配列で返す（基本使わない：テスト用）
	 * @returns {number[]}
	 */
	getUnnamedColumnsIndices() {
		//----------------------------------------------------------------------
		/** @type {RecordAnalyzer[]} */
		const columns = /** @type {RecordAnalyzer[]} */ (this.find("columns").value);
		return columns.reduce((acc, cur, index) => {
			if (typeof cur.getName() === "undefined") {
				acc.push(index);
			}
			return acc;
		}, /** @type {number[]} */([]));
	}

	//**************************************************************************
	/**
	 * name属性未定義カラムの名前をつける（基本使わない）
	 * @param {string[]} names 名前の配列（少なければ放置、多ければ除外）
	 * @returns {this}
	 */
	setNamesForUnnamedColumns(names) {
		//----------------------------------------------------------------------
		/** @type {RecordAnalyzer[]} */
		const columns = /** @type {RecordAnalyzer[]} */ (this.find("columns").value);
		columns.filter(column => typeof column.getName() === "undefined")
			.forEach((column, index) => {
				column.setName(names[index]);
			});
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}
};

//******************************************************************************
// export
export { PointCloudPngParser };
